import { CheckCircle, Edit, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/Table';
import { GetAllCustomersDto } from '@/services/CustomerService';

interface OnBoardingTableProps {
  customers: Partial<GetAllCustomersDto>[];
  perPage: number;
}

export function OnBoardingTable({ customers, perPage }: OnBoardingTableProps) {
  const navigate = useNavigate();
  const StatusIcon = ({ status }: { status: boolean }) => {
    return status ? (
      <CheckCircle className="h-5 w-5 text-green-600" />
    ) : (
      <XCircle className="h-5 w-5 text-red-600" />
    );
  };

  const ProgressBar = ({ progress }: { progress: number }) => (
    <div className="h-2 w-full rounded-full bg-gray-200">
      <div
        className="h-2 rounded-full bg-blue-600 transition-all duration-300"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-2">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="font-semibold">ID</TableHead>
            <TableHead className="w-1/5 font-semibold">Name</TableHead>
            <TableHead className="text-center font-semibold">
              Freemium
            </TableHead>
            <TableHead className="w-[10%] text-center font-semibold">
              Progress
            </TableHead>
            <TableHead className="text-center font-semibold">
              Recommendations
            </TableHead>
            <TableHead className="text-center font-semibold">
              E-commerce Data
            </TableHead>
            <TableHead className="text-center font-semibold">
              Taxonomy
            </TableHead>
            <TableHead className="text-center font-semibold">
              Product Read
            </TableHead>
            <TableHead className="text-center font-semibold">
              Media Cluster
            </TableHead>
            <TableHead className="text-center font-semibold">
              SEO Integrated
            </TableHead>
            <TableHead className="text-center font-semibold">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {customers.map((item: Partial<GetAllCustomersDto>) => (
            <TableRow
              key={item.id}
              className="transition-colors hover:bg-gray-100"
            >
              <TableCell className="font-mono text-sm font-medium text-gray-600">
                {item.id}
              </TableCell>

              <TableCell>
                <div className="flex flex-col">
                  <span className="font-medium text-gray-900">{item.name}</span>
                </div>
              </TableCell>

              <TableCell className="text-center">
                <div className="flex justify-center rounded-sm p-1">
                  <p>{item.freemium ? 'Yes' : 'No'}</p>
                </div>
              </TableCell>

              <TableCell className="text-center">
                <div className="flex items-center justify-center gap-2">
                  <ProgressBar progress={item.metrics?.progress || 0} />
                  <span className="text-sm font-medium text-gray-600">
                    {item.metrics?.progress}%
                  </span>
                </div>
              </TableCell>

              <TableCell className="text-center">
                <div className="flex justify-center rounded-sm p-1">
                  <StatusIcon
                    status={item.metrics?.recommendationImplemented || false}
                  />
                </div>
              </TableCell>

              <TableCell className="text-center">
                <div className="flex justify-center rounded-sm p-1">
                  <StatusIcon status={item.metrics?.ecommerceData || false} />
                </div>
              </TableCell>

              <TableCell className="text-center">
                <div className="flex justify-center rounded-sm p-1">
                  <StatusIcon status={item.metrics?.taxonomy || false} />
                </div>
              </TableCell>

              <TableCell className="text-center">
                <div className="flex justify-center rounded-sm p-1">
                  <StatusIcon status={item.metrics?.productRead || false} />
                </div>
              </TableCell>

              <TableCell className="text-center">
                <div className="flex justify-center rounded-sm p-1">
                  <StatusIcon status={item.metrics?.mediaCluster || false} />
                </div>
              </TableCell>

              <TableCell className="text-center">
                <div className="flex justify-center rounded-sm p-1">
                  <StatusIcon status={item.metrics?.seoIntegrated || false} />
                </div>
              </TableCell>

              <TableCell className="text-center">
                <Button
                  variant="ghost"
                  className="h-8 w-8 hover:bg-white"
                  size="sm"
                  onClick={() => navigate('/admin/customers/edit/' + item.id)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}

          {customers.length &&
            Array.from({ length: perPage - customers.length }).map(
              (_, index) => (
                <TableRow key={index} className="border-0 hover:bg-white">
                  <TableCell colSpan={11} className="h-12" />
                </TableRow>
              ),
            )}
        </TableBody>
      </Table>
    </div>
  );
}
